import React, { useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import orderBy from 'lodash/orderBy';
import styled from 'styled-components';
import { Cell, HeaderCell, HeaderRow, Row, SortLabel, Table, TableBody, TableHead } from 'components';
import { ICollectedTipsData, IPaymentSetCalculations, ITipCalculatorBaseTotals } from 'types';
import { centsToDollarsDisplay, toTitleCase } from 'utils';
import { CSVFormatter } from 'utils/csvformatter';
import { IExportData } from './types';

const TableWrapper = styled.section`
  width: 100%;
`;

interface ICollectedTipsProps {
  header: (data?: IExportData) => React.ReactNode;
  calculations?: IPaymentSetCalculations;
  isLoading?: boolean;
}

export const formatCollectedTipsData = (calculations: IPaymentSetCalculations, sort: { field: string; direction?: 'asc' | 'desc' }): ICollectedTipsData[] => {
  const data: ICollectedTipsData[] = [];

  const getTotals = (totals: ITipCalculatorBaseTotals) => ({
    cashTips: totals.tip.cash,
    cashAutograts: totals.autoGratuity.cash,
    ccTips: totals.tip.creditCard,
    ccAutograts: totals.autoGratuity.creditCard,
    ccFee: totals.tip.creditCardFee + totals.autoGratuity.creditCardFee,
    net: totals.net,
  });

  calculations?.results.forEach((result) => {
    result.employees.forEach((employee) => {
      data.push({
        payee: employee.name,
        daySegment: result.segment,
        jobTitle: employee.job.title,
        ...getTotals(employee.totals),
      });

      employee.splitChecks?.contributions.forEach((contribution) => {
        data.push({
          payee: employee.name,
          daySegment: `Split Check #${contribution.displayId}`,
          ...getTotals(contribution.totals),
        });
      });

      employee.ignoredChecks?.forEach((check) => {
        data.push({
          payee: employee.name,
          daySegment: `Ignored Check #${check.displayId}`,
          jobTitle: employee.job.title,
          ...getTotals(check.totals),
        });
      });
    });
  });

  return orderBy(data, sort.field, sort.direction);
};

const headers = [
  { label: 'Payee', value: 'payee' },
  { label: 'Day Segment', value: 'daySegment' },
  { label: 'Job', value: 'jobTitle' },
  { label: 'Cash Tips', value: 'cashTips' },
  { label: 'Cash Autograts', value: 'cashAutograts' },
  { label: 'CC Tips', value: 'ccTips' },
  { label: 'CC Autograts', value: 'ccAutograts' },
  { label: 'CC Fee', value: 'ccFee' },
  { label: 'Net', value: 'net' },
];

const CollectedTips = ({ header, calculations, isLoading }: ICollectedTipsProps) => {
  const [sortBy, setSortBy] = useState<{ field: string; direction: 'asc' | 'desc' }>({ field: 'daySegment', direction: 'asc' });

  const data = formatCollectedTipsData(calculations, sortBy);
  const exportCollectedTipsCSV = async () => CSVFormatter.collectedTipsPayout(data);

  return (
    <>
      {header({ exportFunc: exportCollectedTipsCSV, fileName: 'CollectedTips' })}
      <TableWrapper data-cy="table">
        <Table scrollable sticky data-cy="collected-tips-table">
          <TableHead>
            <HeaderRow sticky>
              {headers.map(({ label, value }) => (
                <HeaderCell key={`${value}-header`}>
                  <SortLabel
                    label={label}
                    data-cy={`${value}-label`}
                    isSorted={sortBy.field === value}
                    isSortedDesc={sortBy.direction === 'asc'}
                    isLoading={isLoading}
                    onClick={() => setSortBy({ field: value, direction: sortBy.direction === 'asc' ? 'desc' : 'asc' })}
                  />
                </HeaderCell>
              ))}
            </HeaderRow>
          </TableHead>
          <TableBody>
            {data.map((data: ICollectedTipsData) => (
              <Row>
                <Cell wrapText minWidth={150} data-label="Payee" fontWeight="semiBold">
                  {isLoading ? <Skeleton width={50} /> : data.payee || '--'}
                </Cell>
                <Cell wrapText minWidth={150} data-label="DaySegment">
                  {isLoading ? <Skeleton width={50} /> : data.daySegment || '--'}
                </Cell>
                <Cell data-label="JobTitle">{isLoading ? <Skeleton width={50} /> : toTitleCase(data.jobTitle || '--')}</Cell>
                <Cell data-label="CashTips">{isLoading ? <Skeleton width={50} /> : centsToDollarsDisplay(data.cashTips)}</Cell>
                <Cell data-label="CashAutograts">{isLoading ? <Skeleton width={50} /> : centsToDollarsDisplay(data.cashAutograts)}</Cell>
                <Cell data-label="CCTips">{isLoading ? <Skeleton width={50} /> : centsToDollarsDisplay(data.ccTips)}</Cell>
                <Cell data-label="CCAutograts">{isLoading ? <Skeleton width={50} /> : centsToDollarsDisplay(data.ccAutograts)}</Cell>
                <Cell data-label="CCFee">{isLoading ? <Skeleton width={50} /> : centsToDollarsDisplay(data.ccFee)}</Cell>
                <Cell data-label="Net" fontWeight="semiBold">
                  {isLoading ? <Skeleton width={50} /> : centsToDollarsDisplay(data.net)}
                </Cell>
              </Row>
            ))}
          </TableBody>
        </Table>
      </TableWrapper>
    </>
  );
};

export default CollectedTips;
